import { useRef, useState, type FocusEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { isImeComposing } from '@utils/ime';

export interface IUseInlineRenameOptions {
  /** The value the input opens with (the current name). */
  initial: string;
  /** Fired with a trimmed, non-empty value on Enter or a blur that leaves
   *  something to commit. */
  onCommit: (value: string) => void;
  /** Fired on Escape, or a blur that leaves nothing to commit. */
  onCancel: () => void;
}

export interface IUseInlineRenameResult {
  value: string;
  setValue: (value: string) => void;
  handleKeyDown: (e: ReactKeyboardEvent<HTMLInputElement>) => void;
  handleBlur: (e: FocusEvent<HTMLInputElement>) => void;
}

/**
 * Shared mechanics behind every inline rename-in-place `<input>` in
 * Logistics (the delivery board's status rename, the site-map area drawer's
 * rename) — everything except the actual markup, which stays with the
 * caller since the two look nothing alike (a dropdown row vs. a drawer
 * title).
 *
 * Enter commits a trimmed non-empty value; Escape ALWAYS cancels, and
 * — critically — calls `stopPropagation()` on the keydown. Both callers sit
 * inside a container (a Base UI `Dialog`, a custom dropdown) that listens
 * for Escape at the document level to close ITSELF; without stopping
 * propagation here, cancelling the inline edit also closes the surrounding
 * dialog/dropdown, which is not what "Escape" on a text field means. Blur —
 * the fallback for a user who clicks away instead of pressing a key —
 * commits if there is something to commit and cancels otherwise.
 * `settledRef` guards against a double fire (Enter's commit unmounting the
 * input can itself trigger a synthetic blur).
 */
export function useInlineRename({ initial, onCommit, onCancel }: IUseInlineRenameOptions): IUseInlineRenameResult {
  const [value, setValue] = useState(initial);
  const settledRef = useRef(false);

  const commit = () => {
    if (settledRef.current) return;
    const trimmed = value.trim();
    if (!trimmed) return;
    settledRef.current = true;
    onCommit(trimmed);
  };

  const cancel = () => {
    if (settledRef.current) return;
    settledRef.current = true;
    onCancel();
  };

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isImeComposing(e)) {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // Stop this from also bubbling to whatever container-level Escape
      // listener the caller sits inside — Escape here means "cancel this
      // edit", not "close the whole thing around it".
      e.stopPropagation();
      cancel();
    }
  };

  const handleBlur = (_e: FocusEvent<HTMLInputElement>) => {
    if (value.trim()) commit();
    else cancel();
  };

  return { value, setValue, handleKeyDown, handleBlur };
}
